import React, { useState, useRef, useEffect } from "react";
import { v4 as uuidv4 } from 'uuid';
import adaLogo from "../ada-logo.svg";
import "../App.css";
import "./DocumentAnalyzerPage.css";
import { useLoading } from '../App';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function parseJwt(token: string) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

type DocStatus =
  | 'uploading'
  | 'processing'
  | 'ready'
  | 'error'
  | 'failed'
  | 'extracting_text'
  | 'ocr_text'
  | 'generating_embeddings'
  | 'storing_vectors';

const DocumentAnalyzerPage: React.FC = () => {
  // State for uploaded documents and their statuses
  const [docs, setDocs] = useState<{
    docId: string;
    name: string;
    status: DocStatus;
    uploadProgress: number; // 0-100
    error?: string;
    extractedTextPreview?: string;
  }[]>([]);
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [showTypewriter, setShowTypewriter] = useState(false);
  const [isUploading, setIsUploading] = useState(false); // Only one uploading state
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const answerRef = useRef<HTMLDivElement | null>(null);
  const qaSectionRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledToAnswer = useRef(false);
  const [toasts, setToasts] = useState<{ type: 'success' | 'error' | 'info'; message: string }[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  // Track which docs have already shown the 'ready' toast
  const shownReadyToasts = useRef<{ [docId: string]: boolean }>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Header user menu state
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const avatarButtonRef = useRef<HTMLButtonElement | null>(null);
  const [userInfo, setUserInfo] = useState<{ username?: string; email?: string } | null>(null);
  const [password, setPassword] = useState("");
  const pollingInterval = useRef<number | null>(null);

  const { setLoading } = useLoading();

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      window.location.href = "/login";
      return;
    }
    const payload = parseJwt(token);
    setUserInfo({ username: payload?.username, email: payload?.email });

    // Fetch persistent documents for this user
    fetch(`${API_URL}/my_documents/`, {
      headers: { "Authorization": `Bearer ${token}` }
    })
      .then(res => res.json())
      .then((docsFromBackend) => {
        if (Array.isArray(docsFromBackend)) {
          setDocs(prev => {
            const existingIds = new Set(prev.map(d => d.docId));
            const newDocs = docsFromBackend.filter(doc => !existingIds.has(doc.doc_id)).map(doc => ({
              docId: doc.doc_id,
              name: doc.filename,
              status: doc.status,
              uploadProgress: 100,
              error: doc.error,
              extractedTextPreview: doc.extracted_text_preview
            }));
            return [...prev, ...newDocs];
          });
          docsFromBackend.forEach(doc => {
            if (doc.status === 'ready') {
              shownReadyToasts.current[doc.doc_id] = true;
            }
          });
        }
      })
      .catch(() => {});
  }, []);

  // Close user menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node) &&
        avatarButtonRef.current &&
        !avatarButtonRef.current.contains(e.target as Node)
      ) {
        setShowUserMenu(false);
      }
    }
    if (showUserMenu) {
      document.addEventListener('mousedown', handleClick);
    } else {
      document.removeEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showUserMenu]);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    window.location.href = '/login';
  };

  // Upload handler with per-file progress
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    try {
      if (e.target.files) {
        const filesArray = Array.from(e.target.files) as File[];
        if (filesArray.length === 0) {
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }
        // Prevent uploading files with the same name as currently uploaded docs
        const uploadedNames = new Set(docs.map(doc => doc.name));
        const filteredFiles = filesArray.filter(file => !uploadedNames.has(file.name));
        if (filteredFiles.length === 0) {
          setError("All selected files are already uploaded. Remove them first to re-upload.");
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }
        setError("");
        setAnswer("");
        let failedFiles: string[] = [];
        // Add uploading placeholders
        const uploadingPlaceholders = filteredFiles.map(file => ({
          docId: `uploading-${file.name}-${Date.now()}-${uuidv4()}`,
          name: file.name,
          status: 'uploading' as const,
          uploadProgress: 0
        }));
        setDocs(prev => [...prev, ...uploadingPlaceholders]);
        setSelectedDocIds(prev => [...prev, ...uploadingPlaceholders.map(doc => doc.docId)]);
        const token = localStorage.getItem("access_token");
        for (let i = 0; i < filteredFiles.length; i++) {
          const file = filteredFiles[i];
          const placeholderDocId = uploadingPlaceholders[i].docId;
          const formData = new FormData();
          formData.append("file", file);
          if (password) {
            formData.append("password", password);
          }
          try {
            // Set placeholder to 'uploading'
            setDocs(prev => prev.map(doc =>
              doc.docId === placeholderDocId ? { ...doc, status: 'uploading', uploadProgress: 0 } : doc
            ));
            const res = await fetch(`${API_URL}/upload_pdf/`, {
              method: "POST",
              headers: token ? { "Authorization": `Bearer ${token}` } : {},
              body: formData,
            });
            if (res.ok) {
              const data = await res.json();
              if (data.doc_id) {
                // Remove placeholder, add real doc with 'processing' status
                setDocs(prev => {
                  const filtered = prev.filter(doc => doc.docId !== placeholderDocId);
                  if (!filtered.some(doc => doc.docId === data.doc_id)) {
                    filtered.push({
                      docId: data.doc_id,
                      name: file.name,
                      status: 'processing',
                      uploadProgress: 100
                    });
                  }
                  return filtered;
                });
                setSelectedDocIds(prev => prev.map(id => id === placeholderDocId ? data.doc_id : id));
              } else {
                failedFiles.push(file.name);
                setDocs(prev => prev.filter(doc => doc.docId !== placeholderDocId));
                setSelectedDocIds(prev => prev.filter(id => id !== placeholderDocId));
              }
            } else {
              failedFiles.push(file.name);
              setDocs(prev => prev.filter(doc => doc.docId !== placeholderDocId));
              setSelectedDocIds(prev => prev.filter(id => id !== placeholderDocId));
            }
          } catch {
            failedFiles.push(file.name);
            setDocs(prev => prev.filter(doc => doc.docId !== placeholderDocId));
            setSelectedDocIds(prev => prev.filter(id => id !== placeholderDocId));
          }
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (failedFiles.length > 0) {
          setError(`Failed to upload: ${failedFiles.join(', ')}`);
          showToast('error', `Failed to upload: ${failedFiles.join(', ')}`);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Select or deselect a document
  const handleDocSelect = (docId: string) => {
    setSelectedDocIds((prev) =>
      prev.includes(docId)
        ? prev.filter((id) => id !== docId)
        : [...prev, docId]
    );
  };

  // Remove a document from the list
  const handleRemoveDoc = (docId: string) => {
    setDocs((prev) => {
      const updated = prev.filter((doc) => doc.docId !== docId);
      if (updated.length === 0) {
        shownReadyToasts.current = {};
        setError("");
        setQuestion("");
      } else {
        delete shownReadyToasts.current[docId];
      }
      return updated;
    });
    setSelectedDocIds((prev) => prev.filter((id) => id !== docId));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Ask a question about the selected documents
  const handleAsk = async () => {
    setLoading(true);
    try {
      const selectedDocs = docs.filter(doc => selectedDocIds.includes(doc.docId));
      if (selectedDocs.some(doc => doc.status === 'failed' || doc.status === 'error')) {
        setError("One or more selected documents failed to process and cannot be used for Q&A.");
        setLoading(false);
        return;
      }
      if (!selectedDocIds.length || !question) return;
      setGenerating(true);
      setError("");
      setAnswer("");
      setShowTypewriter(false);
      const formData = new FormData();
      formData.append("doc_ids", selectedDocIds.join(","));
      formData.append("question", question);
      const res = await fetch(`${API_URL}/ask_question/`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.answer) {
        setTimeout(() => {
          setShowTypewriter(true);
        }, 100);
        setAnswer(data.answer);
      } else {
        setError("No answer received.");
      }
    } finally {
      setGenerating(false);
      setLoading(false);
    }
  };

  // Scroll to answer when available
  useEffect(() => {
    if (answer && answerRef.current && !hasScrolledToAnswer.current) {
      // Scroll to the top of the answer, not center, and do not focus
      answerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
      hasScrolledToAnswer.current = true;
    }
    if (!answer) {
      hasScrolledToAnswer.current = false;
    }
  }, [answer]);

  // Polling for processing status
  useEffect(() => {
    if (pollingInterval.current) clearInterval(pollingInterval.current);
    const token = localStorage.getItem("access_token");
    const poll = async () => {
      const processingDocs = docs.filter(doc => doc.status === 'processing' || doc.status === 'extracting_text' || doc.status === 'ocr_text' || doc.status === 'generating_embeddings' || doc.status === 'storing_vectors');
      if (processingDocs.length === 0) return;
      await Promise.all(processingDocs.map(async (doc) => {
        try {
          const res = await fetch(`${API_URL}/pdf_status/?doc_id=${encodeURIComponent(doc.docId)}`, {
            headers: token ? { "Authorization": `Bearer ${token}` } : {},
          });
          if (res.status === 404) return;
          const data = await res.json();
          if (data.status && data.status !== doc.status) {
            setDocs(prev => prev.map(d => d.docId === doc.docId ? { ...d, status: data.status, error: data.error } : d));
          }
        } catch {}
      }));
    };
    pollingInterval.current = window.setInterval(poll, 2000);
    return () => {
      if (pollingInterval.current) clearInterval(pollingInterval.current);
    };
  }, [docs]);

  // Toast notification helpers
  const showToast = (type: 'success' | 'error' | 'info', message: string) => {
    setToasts((prev) => [...prev, { type, message }]);
    setTimeout(() => setToasts((prev) => prev.slice(1)), 3500);
  };

  useEffect(() => {
    if (isUploading) showToast('info', 'Uploading PDF(s)...');
  }, [isUploading]);

  useEffect(() => {
    docs.forEach(doc => {
      const status = doc.status;
      if (status === 'ready' && !shownReadyToasts.current[doc.docId]) {
        showToast('success', `PDF "${doc.name}" is ready!`);
        shownReadyToasts.current[doc.docId] = true;
      }
      if (status === 'error') {
        showToast('error', `PDF "${doc.name}" failed to process.`);
        // Deselect errored files
        setSelectedDocIds(prev => prev.filter(id => id !== doc.docId));
      }
    });
  }, [docs]);

  // Progress bar logic
  const totalDocs = docs.length;
  const uploadingDocs = docs.filter(doc => doc.status === 'uploading');
  const processingDocs = docs.filter(doc => doc.status === 'processing');
  const readyDocs = docs.filter(doc => doc.status === 'ready').length;
  const errorDocs = docs.filter(doc => doc.status === 'error').length;
  const globalProgress = totalDocs === 0 ? 0 : Math.round((readyDocs / totalDocs) * 100);
  
  // Show upload progress bar when actively uploading files
  const showUploadProgressBar = isUploading && uploadingDocs.length > 0;
  // Show processing progress bar when there are documents that are not ready yet (excluding errors)
  const showProcessingProgressBar = totalDocs > 0 && readyDocs < (totalDocs - errorDocs) && !showUploadProgressBar;
  
  // Smooth progress bar: never jump backwards
  const [displayProgress, setDisplayProgress] = useState(0);
  useEffect(() => {
    if (showUploadProgressBar) {
      // For upload progress, use the actual upload progress
      setDisplayProgress(prev => Math.max(prev, uploadProgress));
    } else if (showProcessingProgressBar) {
      // For processing progress, calculate based on ready vs total docs (excluding errors)
      const activeDocs = totalDocs - errorDocs;
      const processingProgress = activeDocs > 0 ? Math.round((readyDocs / activeDocs) * 100) : 0;
      setDisplayProgress(prev => Math.max(prev, processingProgress));
    } else {
      // Reset when no progress bars should be shown
      setDisplayProgress(0);
    }
  }, [uploadProgress, showUploadProgressBar, showProcessingProgressBar, readyDocs, totalDocs, errorDocs]);
  const progressPercent = displayProgress;

  const handleDeleteDoc = async (docId: string) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      if (!token) return;
      const res = await fetch(`${API_URL}/delete_document/${encodeURIComponent(docId)}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        setDocs(prev => prev.filter(doc => doc.docId !== docId));
        setSelectedDocIds(prev => prev.filter(id => id !== docId));
        showToast('success', 'Document deleted.');
      } else {
        showToast('error', 'Failed to delete document.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <header className="analyzer-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src={adaLogo} alt="ADA Logo" style={{ width: 44, height: 32, borderRadius: 8, background: '#fff', boxShadow: '0 1px 4px #181c2f22' }} />
        </div>
        <div style={{ position: 'relative' }}>
          <button
            ref={avatarButtonRef}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              margin: 0,
              outline: 'none',
              display: 'flex',
              alignItems: 'center',
              borderRadius: '50%',
              width: 40,
              height: 40,
              justifyContent: 'center',
              transition: 'background 0.2s',
              boxShadow: showUserMenu ? '0 2px 8px #29b6f655' : undefined,
              backgroundColor: showUserMenu ? '#232946' : 'transparent',
            }}
            onClick={() => {
              setShowUserMenu(v => !v);
            }}
            aria-label="User menu"
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="14" cy="14" r="14" fill="#6ee7ff" />
              <ellipse cx="14" cy="11.5" rx="5.5" ry="5.5" fill="#232946" />
              <ellipse cx="14" cy="21" rx="8" ry="4" fill="#232946" />
            </svg>
          </button>
          {showUserMenu && (
            <div ref={userMenuRef} style={{
              position: 'absolute',
              right: 0,
              top: 48,
              background: '#232946',
              color: '#fff',
              borderRadius: 12,
              boxShadow: '0 4px 24px #181c2f55',
              minWidth: 220,
              padding: '18px 22px',
              zIndex: 1001,
              fontSize: '1rem',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              animation: 'fadeIn 0.2s',
            }}>
              <div style={{ fontWeight: 700, fontSize: '1.08rem', color: '#6ee7ff', marginBottom: 2 }}>{userInfo?.username || 'User'}</div>
              <div style={{ color: '#ffffff', fontSize: '0.95rem', marginBottom: 8, opacity: 0.9, wordBreak: 'break-all' }}>{userInfo?.email || 'No email'}</div>
              <button
                onClick={handleLogout}
                style={{
                  background: '#e74c3c',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 0',
                  fontWeight: 700,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  marginTop: 8,
                  boxShadow: '0 2px 8px #e74c3c33',
                  transition: 'background 0.2s',
                }}
              >
                Log out
              </button>
            </div>
          )}
        </div>
      </header>
      <div className="App">
        <div className="bg-shape1"></div>
        <div className="bg-shape2"></div>
        <div className="bg-shape3"></div>
        {/* ADA Logo */}
        <div className="ada-logo">
          <img src={adaLogo} alt="ADA Logo" className="ada-logo" />
        </div>
        <h1 className="fadeIn">AI Document Analyzer</h1>
        <div className="subtitle">Ask questions about your documents with AI</div>
        {/* Upload Button Section */}
        <div className="card" style={{ marginTop: 24 }}>
          <div className="upload-section">
            <input
              id="pdf-upload"
              className="custom-file-input"
              type="file"
              accept=".pdf,.txt,.md,.docx,application/pdf,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              multiple
              onChange={handleFileChange}
              disabled={isUploading}
              ref={fileInputRef}
              onClick={e => { (e.target as HTMLInputElement).value = ''; }}
            />
            <label htmlFor="pdf-upload" className="custom-file-label">
              Upload Document(s)
            </label>
            {/* <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input
                type="password"
                placeholder="PDF Password (if encrypted)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
                disabled={isUploading}
              />
              <div style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                Leave empty if your PDF is not password-protected
              </div>
            </div> */}
          </div>
        </div>

        {/* Uploaded Files Section */}
        {docs.length > 0 && (
          <div className="card" style={{ marginTop: 18 }}>
            <div className="file-list">
              <h3>Uploaded Files</h3>
              <ul>
                {docs.map((doc) => {
                  const status = doc.status;
                  const isReady = status === 'ready';
                  const isError = status === 'failed' || status === 'error';
                  const isProcessing = !isReady && !isError;
                  // Map status to user-friendly label
                  let statusLabel = '';
                  if (status === 'extracting_text') statusLabel = 'Extracting text...';
                  else if (status === 'ocr_text') statusLabel = 'Running OCR...';
                  else if (status === 'generating_embeddings') statusLabel = 'Generating embeddings...';
                  else if (status === 'storing_vectors') statusLabel = 'Storing vectors...';
                  else if (status === 'processing' || status === 'uploading') statusLabel = 'Processing...';
                  else if (status === 'ready') statusLabel = 'Ready';
                  else if (status === 'failed' || status === 'error') statusLabel = 'Failed';
                  return (
                    <li
                      key={doc.docId}
                      className={selectedDocIds.includes(doc.docId) ? "selected" : ""}
                      onClick={() => isReady && handleDocSelect(doc.docId)}
                      style={{
                        opacity: isReady ? 1 : 0.6,
                        pointerEvents: (isReady || isError) ? 'auto' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        minHeight: 40,
                        paddingRight: 8
                      }}
                    >
                      <span
                        className={selectedDocIds.includes(doc.docId) ? "selected" : ""}
                        style={{
                          flexGrow: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                          verticalAlign: 'middle',
                        }}
                      >
                        {doc.name}
                      </span>
                      {/* Per-file progress bar */}
                      {doc.status === 'uploading' && (
                        <span style={{ marginLeft: 0, marginRight: 4, flexShrink: 0, width: 80 }}>
                          <div style={{ height: 6, background: '#29b6f6', width: `${doc.uploadProgress}%`, borderRadius: 3, transition: 'width 0.2s' }} />
                          <span style={{ marginLeft: 6, fontSize: 13, color: '#888' }}>{doc.uploadProgress}%</span>
                        </span>
                      )}
                      {isProcessing && (
                        <span style={{ marginLeft: 0, marginRight: 4, flexShrink: 0 }}>
                          <span className="spinner" style={{ width: 16, height: 16 }} />
                          <span style={{ marginLeft: 6, fontSize: 13, color: '#888' }}>{statusLabel}</span>
                        </span>
                      )}
                      {isError && <span style={{ marginLeft: 12, color: '#e74c3c', fontWeight: 500 }}>Error: {doc.error || 'Unknown error'}</span>}
                      {(isReady || isError) && (
                        <button
                          style={{
                            marginLeft: 4,
                            background: "#e74c3c",
                            color: "#fff",
                            borderRadius: "50%",
                            border: "none",
                            width: 28,
                            height: 28,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 18,
                            cursor: "pointer",
                            boxShadow: "0 1px 4px #e74c3c22",
                            flexShrink: 0,
                            padding: 0,
                            transition: 'background 0.2s',
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteDoc(doc.docId);
                          }}
                          title="Delete document"
                        >
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3.5 3.5L10.5 10.5" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                            <path d="M10.5 3.5L3.5 10.5" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                        </button>
                      )}
                      {/* Optionally, show extracted text preview for admin/debugging */}
                      {doc.extractedTextPreview && (
                        <div style={{ marginTop: 8, fontSize: 12, color: '#888', maxWidth: 400, whiteSpace: 'pre-wrap', background: '#f7fbfc', borderRadius: 8, padding: 8 }}>
                          <strong>Extracted Preview:</strong> {doc.extractedTextPreview}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
        {docs.length === 0 && (
          <div className="card" style={{marginTop: 18, textAlign: 'center', color: '#039be5', fontWeight: 600}}>
            Please upload and select at least one PDF to ask a question.
          </div>
        )}

        {/* Q&A and Answer Section */}
        <div className="card" style={{ marginTop: 18 }}>
          <div ref={qaSectionRef} className="qa-section" style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder={
                (!selectedDocIds.length || generating || docs.filter(doc => selectedDocIds.includes(doc.docId)).some(doc => doc.status !== 'ready'))
                  ? ''
                  : 'Ask a question'
              }
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (
                  e.key === 'Enter' &&
                  selectedDocIds.length > 0 &&
                  question &&
                  !generating &&
                  selectedDocIds.every(id => {
                    const doc = docs.find(d => d.docId === id);
                    return doc && doc.status === 'ready';
                  })
                ) {
                  e.preventDefault();
                  handleAsk();
                }
              }}
              disabled={
                !selectedDocIds.length ||
                generating ||
                docs.filter(doc => selectedDocIds.includes(doc.docId)).some(doc => doc.status !== 'ready')
              }
            />
            <button
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                handleAsk();
              }}
              disabled={
                !selectedDocIds.length ||
                !question ||
                generating ||
                docs.filter(doc => selectedDocIds.includes(doc.docId)).some(doc => doc.status !== 'ready')
              }
            >
              {generating ? <span className="spinner" /> : "Ask"}
            </button>
          </div>
          {generating && (
            <div className="answer">Generating answer...</div>
          )}
          {answer && (
            <div ref={answerRef} tabIndex={-1} className="answer" style={{ minHeight: showTypewriter ? 64 : undefined }}>
              <strong style={{ marginBottom: 8}}>Answer:</strong>
              {showTypewriter ? (
                <span className="typewriter">{answer}</span>
              ) : (
                <span style={{ opacity: 0 }}>{answer}</span>
              )}
            </div>
          )}
          {error && <div className="error">{error}</div>}
        </div>
        {/* Global Progress Bar */}
        {(showUploadProgressBar || showProcessingProgressBar) && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', zIndex: 1000 }}>
            <div style={{
              height: 6,
              background: showUploadProgressBar ? '#29b6f6' : '#26c6da',
              width: `${progressPercent}%`,
              transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
              boxShadow: '0 2px 8px #29b6f655',
              borderRadius: 3
            }} />
          </div>
        )}
        {/* Toast Notifications */}
        <div style={{ position: 'fixed', top: 18, right: 18, zIndex: 2000 }}>
          {toasts.map((toast, i) => (
            <div key={i} style={{
              background: toast.type === 'success' ? '#26c6da' : toast.type === 'error' ? '#e74c3c' : '#b3c6fc',
              color: '#fff',
              borderRadius: 10,
              padding: '12px 22px',
              marginBottom: 10,
              boxShadow: '0 2px 12px #29b6f655',
              fontWeight: 600,
              minWidth: 220,
              fontSize: '1.05rem',
              letterSpacing: 0.5,
              opacity: 0.97
            }}>{toast.message}</div>
          ))}
        </div>
      </div>
    </>
  );
}

export default DocumentAnalyzerPage;

/* Fade-in animation for answer */
const style = document.createElement('style');
style.innerHTML = `
.fade-in-anim {
  animation: fadeInAnswer 0.7s cubic-bezier(0.39, 0.575, 0.565, 1) both;
}
@keyframes fadeInAnswer {
  0% { opacity: 0; transform: translateY(30px); }
  100% { opacity: 1; transform: translateY(0); }
}
`;
document.head.appendChild(style);